import React, { useEffect } from 'react';
import { X, ShieldCheck, Scale, FileText, Lock } from 'lucide-react';
import { Language } from '../types';

interface LegalModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'privacy' | 'terms' | null;
  currentLang: Language;
}

export const LegalModal: React.FC<LegalModalProps> = ({ isOpen, onClose, type, currentLang }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen || !type) return null;

  const content = {
    privacy: {
      title: {
        fr: 'Politique de Confidentialité et de Sécurité',
        en: 'Privacy & Security Policy',
        ar: 'سياسة الخصوصية والأمن'
      },
      icon: <ShieldCheck className="w-6 h-6" />,
      body: {
        fr: (
          <div className="space-y-6">
            <p>
              Bienvenue sur le site de <strong>CHERIF HADDAD Consulting & Expertise</strong>. La protection de vos données personnelles et le maintien d'une transparence absolue concernant leur traitement sont au cœur de nos préoccupations et de nos valeurs professionnelles.
            </p>

            <section>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2 flex items-center gap-2">
                <Scale className="w-5 h-5 text-slate-500" />
                1. Conformité Réglementaire (Loi 18-07)
              </h3>
              <p>
                Nous nous engageons formellement à respecter la <strong>Loi n° 18-07 du 10 juin 2018 relative à la protection des personnes physiques dans le traitement des données à caractère personnel</strong>, ainsi que les principes généraux du RGPD. Toutes les données collectées (noms, emails, numéros de téléphone via notre formulaire de contact, journaux de vérification des certificats) sont traitées de manière licite, loyale et transparente.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2 flex items-center gap-2">
                <Lock className="w-5 h-5 text-slate-500" />
                2. Sécurité Informatique et Normes (ISO 27001)
              </h3>
              <p>
                En tant que cabinet d'expertise, nous appliquons des mesures de sécurité de haut niveau, alignées sur les exigences de la norme <strong>ISO/IEC 27001</strong>. Notre infrastructure intègre :
              </p>
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>Le chiffrement systématique des flux d'informations (certificats SSL/TLS).</li>
                <li>Des contrôles d'accès administratifs stricts (authentification avec blocage des tentatives, tokens d'accès uniques envoyés par email).</li>
                <li>L'hébergement sécurisé sur des architectures résilientes (Cloud Run) limitant l'exposition aux vulnérabilités.</li>
              </ul>
            </section>

            <section>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2 flex items-center gap-2">
                <FileText className="w-5 h-5 text-slate-500" />
                3. Finalités et Transparence du Traitement
              </h3>
              <p>
                Vos informations ne sont utilisées qu'aux fins explicites suivantes :
              </p>
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>Répondre à vos demandes de conseil, d'audit ou de formation via notre formulaire.</li>
                <li>Permettre la vérification authentique et transparente des certificats professionnels que nous émettons.</li>
                <li>Garantir la sécurité du système contre les accès non autorisés (logs de sécurité).</li>
              </ul>
              <p className="mt-2">
                En aucun cas vos données ne seront cédées, vendues ou louées à des tiers.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">4. Conservation et Droits des Personnes</h3>
              <p>
                Nous conservons vos données uniquement pendant la durée strictement nécessaire à l'accomplissement des finalités évoquées. Conformément à la Loi 18-07, vous disposez d'un droit d'accès, de rectification, et de suppression de vos données personnelles. Vous pouvez exercer ces droits en nous contactant à l'adresse email : <strong>contact@cherifhaddad.com</strong>.
              </p>
            </section>
          </div>
        ),
        en: (
          <div className="space-y-6">
            <p>Welcome to <strong>CHERIF HADDAD Consulting & Expertise</strong>. The protection of your personal data and transparency regarding its processing are at the core of our professional values.</p>
            <p>We are fully compliant with the <strong>Law No. 18-07 of June 10, 2018 (Algeria)</strong> on the protection of individuals in the processing of personal data, and apply international best practices aligned with ISO/IEC 27001 for IT security.</p>
            <p>Your data is encrypted, securely stored, and strictly used for corresponding with you and verifying professional certificates. We do not sell or share your data with third parties.</p>
            <p>You have the right to access, rectify, or delete your data by contacting us at <strong>contact@cherifhaddad.com</strong>.</p>
          </div>
        ),
        ar: (
          <div className="space-y-6 text-right">
            <p>
              مرحباً بكم في الموقع الرسمي لـ <strong>شريف حداد للاستشارات والخبرة (CHERIF HADDAD Consulting & Expertise)</strong>. إن حماية بياناتكم الشخصية والحفاظ على الشفافية التامة بشأن معالجتها يشكلان ركيزة أساسية من قيمنا المهنية والتزاماتنا الأخلاقية.
            </p>

            <section>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2 flex items-center gap-2 justify-start">
                <Scale className="w-5 h-5 text-slate-500" />
                1. الامتثال القانوني والتشريعي (القانون 18-07)
              </h3>
              <p>
                نلتزم التزاماً صارماً بأحكام <strong>القانون الجزائري رقم 18-07 المؤرخ في 10 يونيو 2018 المتعلق بحماية الأشخاص الطبيعيين في معالجة المعطيات ذات الطابع الشخصي</strong>، والمبادئ التوجيهية للائحة العامة لحماية البيانات (GDPR). كافة البيانات المجمعة تخضع لمعالجة قانونية ونزيهة وشفافة.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2 flex items-center gap-2 justify-start">
                <Lock className="w-5 h-5 text-slate-500" />
                2. الأمن السيبراني والمعايير الدولية (ISO/IEC 27001)
              </h3>
              <p>
                بصفتنا هيئة تدقيق واستشارات معتمدة، نطبق أعلى معايير الأمان الرقمي المتوافقة مع متطلبات المعيار الدولي <strong>ISO/IEC 27001</strong>، بما يشمل التشفير المتقدم وتأمين تدفقات البيانات وضوابط التحقق المشددة.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2 flex items-center gap-2 justify-start">
                <FileText className="w-5 h-5 text-slate-500" />
                3. أغراض المعالجة ومصداقية التحقق
              </h3>
              <p>
                تُستخدم بياناتكم حصرياً للإجابة على استفساراتكم وتأطير مهام التدقيق والاستشارات، والتحقق الموثوق من أصالة الشهادات المهنية الصادرة عبر السجل الرسمي. لا يتم إطلاقاً بيع أو مشاركة بياناتكم مع أي أطراف تجارية ثالثة.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">4. حقوق المستخدم والتواصل</h3>
              <p>
                لكم الحق القانوني الكامل في الاطلاع على بياناتكم أو تعديلها أو طلب مسحها. لممارسة هذه الحقوق، يمكنكم مراسلتنا مباشرة عبر البريد الإلكتروني: <strong>contact@cherifhaddad.com</strong>.
              </p>
            </section>
          </div>
        )
      }
    },
    terms: {
      title: {
        fr: 'Conditions Générales d\'Utilisation',
        en: 'Terms of Service',
        ar: 'الشروط العامة للاستخدام'
      },
      icon: <FileText className="w-6 h-6" />,
      body: {
        fr: (
          <div className="space-y-6">
            <p>
              L'utilisation du portail web <strong>CHERIF HADDAD Consulting & Expertise</strong> implique l'acceptation pleine et entière des conditions générales d'utilisation décrites ci-après.
            </p>

            <section>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">1. Accès et Utilisation des Services</h3>
              <p>
                Ce site a pour vocation de fournir des informations concernant les services de conseil, d'audit, d'expertise et de formation du cabinet. Il met également à disposition un outil de <strong>vérification de certificats professionnels</strong>. L'utilisation de cet outil de vérification doit se faire de manière légitime afin de contrôler l'authenticité d'une compétence validée.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">2. Propriété Intellectuelle</h3>
              <p>
                La structure générale du site, ainsi que les textes, graphiques, images, sons et vidéos la composant, sont la propriété de l'éditeur ou de ses partenaires. Toute représentation et/ou reproduction et/ou exploitation partielle ou totale des contenus et services proposés par le site, par quelque procédé que ce soit, sans l'autorisation préalable et par écrit est strictement interdite.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">3. Exactitude des Informations</h3>
              <p>
                Les informations fournies sur le site le sont à titre indicatif. <strong>CHERIF HADDAD Consulting & Expertise</strong> s'efforce d'assurer au mieux l'exactitude et la mise à jour des informations diffusées. Toutefois, le cabinet décline toute responsabilité pour toute imprécision, inexactitude ou omission portant sur des informations disponibles sur le site.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">4. Responsabilité et Sécurité</h3>
              <p>
                Nous mettons en œuvre tous les moyens nécessaires pour garantir la sécurité et la disponibilité du site (respect des normes de cybersécurité, authentification à facteurs). Cependant, la responsabilité du cabinet ne saurait être engagée en cas de force majeure ou du fait imprévisible et insurmontable d'un tiers.
              </p>
            </section>
          </div>
        ),
        en: (
          <div className="space-y-6">
            <p>The use of the <strong>CHERIF HADDAD Consulting & Expertise</strong> web portal implies full acceptance of the general conditions of use described below.</p>
            <p>This site provides information regarding consulting, audit, and training services, as well as a professional certificate verification tool. You agree to use these services legitimately.</p>
            <p>All content and intellectual property on this site belong to the firm. Reproduction without written consent is prohibited. While we strive to maintain high accuracy and security (including 2FA and encryption), the firm cannot be held liable for unforeseen technical disruptions.</p>
          </div>
        ),
        ar: (
          <div className="space-y-6 text-right">
            <p>
              يخضع تصفح واستخدام بوابة <strong>شريف حداد للاستشارات والخبرة</strong> للقبول التام للشروط والأحكام العامة المنصوص عليها أدناه.
            </p>

            <section>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">1. نطاق الخدمات وبوابة التحقق</h3>
              <p>
                يهدف الموقع إلى التعريف ببرامج الاستشارات والتدقيق والتكوين المعتمدة، وتوفير أداة رسمية مشفرة للتحقق من مصداقية الشهادات المهنية. يلتزم المستخدم باستعمال هذه المنظومة للأغراض المشروعة والمصرح بها فقط.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">2. الملكية الفكرية وحقوق النشر</h3>
              <p>
                جميع النصوص، الشعارات، التصاميم والمحتويات المتاحة عبر الموقع محمية بموجب قوانين الملكية الفكرية. يُحظر أي نسخ أو استغلال جزئي أو كلي دون ترخيص خطي مسبق.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">3. الموثوقية وتحديث البيانات</h3>
              <p>
                يحرص المكتب على دقة وحداثة البيانات المنشورة، مع اتخاذ كافة تدابير الحماية الرقمية والجاهزية التقنية لضمان استمرارية الخدمات.
              </p>
            </section>
          </div>
        )
      }
    }
  };

  const selectedContent = content[type];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
      <div 
        className="absolute inset-0 bg-slate-900/60 dark:bg-slate-950/80 backdrop-blur-sm" 
        onClick={onClose}
      />
      
      <div 
        className="relative bg-white dark:bg-slate-900 w-full w-[90%] lg:w-[70%] max-w-none max-h-[90vh] rounded-sm shadow-2xl flex flex-col animate-in fade-in zoom-in-95 duration-200 border border-slate-200 dark:border-slate-800"
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-800">
          <div className="flex items-center gap-3 text-slate-900 dark:text-white">
            <div className="p-2 bg-slate-100 dark:bg-slate-800 rounded-sm">
              {selectedContent.icon}
            </div>
            <h2 className="text-xl font-bold font-montserrat tracking-wide">
              {selectedContent.title[currentLang] || selectedContent.title['fr']}
            </h2>
          </div>
          <button 
            onClick={onClose}
            className="p-2 text-slate-500 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 rounded-sm transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-6 overflow-y-auto custom-scrollbar text-sm text-slate-700 dark:text-slate-300 leading-relaxed font-sans">
          {selectedContent.body[currentLang] || selectedContent.body['fr']}
        </div>
        
        <div className="p-6 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 flex justify-end rtl:justify-start">
          <button 
            onClick={onClose}
            className="px-6 py-2.5 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-sm font-semibold hover:bg-slate-800 dark:hover:bg-slate-200 transition-colors text-sm uppercase tracking-wider"
          >
            {currentLang === 'ar' ? 'إغلاق' : currentLang === 'en' ? 'Close' : 'Fermer'}
          </button>
        </div>
      </div>
    </div>
  );
};
